import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { loadPendingCountsSuccess } from '@app/core/store/pendingCounts/pendingCounts.actions';
import { ApiResponse, IPendingCounts } from '@shared';
import { BehaviorSubject, Subscription, interval, startWith, switchMap } from 'rxjs';
import { CountsService } from './counts.service';

type Watched = 'pendingJobs' | 'pendingPosts' | 'pendingInvoices';

const POLL_MS = 30_000;
const SOUND_URL = 'assets/sounds/admin-pending.mp3';
const PROMPT_SNOOZE_KEY = 'nirlat.adminNotiPromptSnoozedUntil';
const PROMPT_SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

// what each pending counter means, where a click takes the admin
const WATCHED: Record<Watched, { title: string; url: string }> = {
  pendingJobs: { title: 'עבודה חדשה ממתינה לאישור', url: '/jobs' },
  pendingPosts: { title: 'פוסט חדש ממתין לאישור', url: '/nirlat-team' },
  pendingInvoices: { title: 'חשבונית חדשה נסרקה', url: '/scanned-invoices' },
};

export type AdminNotificationState = 'unsupported' | 'default' | 'denied' | 'enabled';

/**
 * Back-office notifications (07/10/2026) — two layers:
 *  A. while the admin is open in ANY tab: polls the pending counters every 30 s and, when one grows, plays the admin's
 *     own sound (assets/sounds/admin-pending.mp3) and shows a browser notification. The sidebar badges update too.
 *  B. Web Push through /push-sw.js, so a notification arrives even with every admin tab closed — with the SYSTEM
 *     sound (browsers ignore custom push sounds). When a tab is open the worker shows it silent and tells the tab to
 *     play the custom sound, so the admin never hears both.
 * Layer B needs the backend's WEB_PUSH config; without it only A runs.
 */
@Injectable({ providedIn: 'root' })
export class AdminNotificationsService implements OnDestroy {
  readonly state$ = new BehaviorSubject<AdminNotificationState>(this.permissionState());
  private poll?: Subscription;
  private last: Partial<Record<Watched, number>> | null = null;
  private pushActive = false;
  private audio?: HTMLAudioElement;
  private lastSoundAt = 0;

  constructor(
    private http: HttpClient,
    private counts: CountsService,
    private store: Store,
    private router: Router,
    private zone: NgZone,
  ) {}

  /** Called once by the logged-in layout. */
  start() {
    if (this.poll) return;
    this.audio = new Audio(SOUND_URL);
    this.audio.preload = 'auto';
    this.poll = interval(POLL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.counts.getPendingCounts()),
      )
      .subscribe({ next: (res: ApiResponse) => res?.status && this.onCounts(res.data), error: () => undefined });
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', this.onWorkerMessage);
    }
    // already allowed on this browser → make sure the push subscription exists (silently)
    if (this.permissionState() === 'enabled') this.subscribePush().catch(() => undefined);
  }

  ngOnDestroy() {
    this.poll?.unsubscribe();
    if ('serviceWorker' in navigator) navigator.serviceWorker.removeEventListener('message', this.onWorkerMessage);
  }

  /** The welcome prompt: only while this browser has not decided yet, and not within a week of "not now". */
  shouldPrompt(): boolean {
    if (this.permissionState() !== 'default') return false;
    try {
      return Number(localStorage.getItem(PROMPT_SNOOZE_KEY) || 0) < Date.now();
    } catch {
      return true;
    }
  }

  snoozePrompt() {
    try {
      localStorage.setItem(PROMPT_SNOOZE_KEY, String(Date.now() + PROMPT_SNOOZE_MS));
    } catch {
      // private mode: it simply asks again next time
    }
  }

  /** From a click (browsers require a user gesture for the permission prompt). */
  async enable(): Promise<AdminNotificationState> {
    if (!('Notification' in window)) return this.setState('unsupported');
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return this.setState(permission === 'denied' ? 'denied' : 'default');
    // a click is also what browsers want before they let the page play a sound later
    this.audio
      ?.play()
      .then(() => this.audio?.pause())
      .catch(() => undefined);
    await this.subscribePush().catch((e) => console.warn('Web push subscribe failed', e));
    return this.setState('enabled');
  }

  private permissionState(): AdminNotificationState {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission === 'granted'
      ? 'enabled'
      : Notification.permission === 'denied'
        ? 'denied'
        : 'default';
  }

  private setState(state: AdminNotificationState) {
    this.state$.next(state);
    return state;
  }

  private async subscribePush() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    const keyRes = await this.http.get<ApiResponse>('v1/admin/web-push/public-key').toPromise();
    const publicKey: string | null = keyRes?.data?.publicKey;
    if (!publicKey) return; // backend has no WEB_PUSH config → polling only
    const registration = await navigator.serviceWorker.register('/push-sw.js', { scope: '/' });
    await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(publicKey),
      });
    }
    await this.http
      .post<ApiResponse>('v1/admin/web-push/subscribe', { subscription: subscription.toJSON() })
      .toPromise();
    this.pushActive = true;
  }

  private onCounts(data: IPendingCounts) {
    this.store.dispatch(loadPendingCountsSuccess({ pendingCounts: data }));
    const current: Partial<Record<Watched, number>> = {};
    (Object.keys(WATCHED) as Watched[]).forEach((k) => (current[k] = Number((data as any)?.[k] ?? 0)));
    const previous = this.last;
    this.last = current;
    if (!previous) return; // first poll after load is the baseline, not news
    (Object.keys(WATCHED) as Watched[])
      .filter((k) => (current[k] ?? 0) > (previous[k] ?? 0))
      .forEach((k) => this.announce(k, (current[k] ?? 0) - (previous[k] ?? 0)));
  }

  private announce(kind: Watched, added: number) {
    this.playSound();
    // with Web Push active the service worker already showed the notification — don't show it twice
    if (this.pushActive || this.permissionState() !== 'enabled') return;
    const { title, url } = WATCHED[kind];
    const n = new Notification(title, {
      body: added > 1 ? `${added} חדשים` : '',
      tag: kind,
      dir: 'rtl',
      lang: 'he',
      icon: 'assets/images/logo/main_logo_sm.png',
    });
    n.onclick = () => {
      window.focus();
      this.zone.run(() => this.router.navigateByUrl(url));
      n.close();
    };
  }

  // the worker tells an open tab about a push → play our sound now, refresh the badges
  private onWorkerMessage = (event: MessageEvent) => {
    if (event?.data?.type !== 'admin-push') return;
    this.zone.run(() => {
      this.playSound();
      this.counts.getPendingCounts().subscribe((res) => res?.status && this.onCountsSilently(res.data));
    });
  };

  private onCountsSilently(data: IPendingCounts) {
    this.store.dispatch(loadPendingCountsSuccess({ pendingCounts: data }));
    const current: Partial<Record<Watched, number>> = {};
    (Object.keys(WATCHED) as Watched[]).forEach((k) => (current[k] = Number((data as any)?.[k] ?? 0)));
    this.last = current; // already announced by the push — the next poll must not announce it again
  }

  private playSound() {
    // one sound for a burst (push + poll can land seconds apart)
    if (Date.now() - this.lastSoundAt < 5000) return;
    this.lastSoundAt = Date.now();
    if (!this.audio) this.audio = new Audio(SOUND_URL);
    this.audio.currentTime = 0;
    this.audio.play().catch(() => undefined); // blocked until the admin has clicked somewhere on the page
  }

  private urlBase64ToUint8Array(base64: string) {
    const padding = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
  }
}
